/**
 * Itadakimasu theme behaviour: vanilla JS, no dependencies.
 *
 * This file reproduces the interactive parts of the React site (src/components/*.jsx) on top
 * of the Liquid markup:
 *
 *   1. Header:   .header gets .header--solid once the page has scrolled.
 *   2. Menu:     the mobile menu (.menu-btn / #site-nav / .menu-scrim, Escape, focus, inert, scroll lock).
 *   3. Reveal:   .reveal gets .is-in the first time it scrolls into view.
 *   4. Drawer:   the bag drawer (open/close, focus trap, focus return, Escape, scroll lock).
 *   5. Cart:     AJAX add (form[data-itk-add]) and change ([data-itk-line][data-itk-qty]); the drawer
 *                and the header bag count are re-rendered with the Section Rendering API.
 *   6. Qty:      quantity steppers (.qty with [data-qty-step] buttons).
 *   7. Buy bar:  the sticky product-page buy bar (shown once the price and buy rows are scrolled past,
 *                hidden over the cross-sell card, FAQ, reviews and footer). body.itk-buybar-on while shown.
 *   8. Video:    the unboxing video loads lazily and plays only while on screen.
 *   9. Marquee:  the scrolling banner pause button; the banner holds still while scrolled out of view.
 *  10. Footer:   the newsletter thank-you line shows only after the footer form itself was posted.
 *  11. Posters:  the loading spinner of a 3D island far off screen holds still until the island comes near.
 *
 * Everything degrades gracefully. Without JS, or when an AJAX request fails, cart forms
 * post normally and the bag link opens the /cart page.
 *
 * Small public API (window.ITK):
 *   ITK.cart.open(trigger?) / ITK.cart.close() / ITK.cart.refresh()
 *   ITK.formatMoney(cents)
 * Events dispatched on document: itk:cart:add, itk:cart:change, itk:cart:refresh, and
 * itk:cart-updated ({ detail: { source: 'drawer' } }) after any bag change made here;
 * itk:qty-change is dispatched on a .qty stepper.
 */
(function () {
  'use strict';

  const ITK = (window.ITK = window.ITK || {});
  if (ITK.themeReady) return; // never bind twice if the script is included twice
  ITK.themeReady = true;

  /* ------------------------------------------------------------------ config */

  const routes = ITK.routes || {};
  const CART_ADD_URL = routes.cart_add_url || '/cart/add';
  const CART_CHANGE_URL = routes.cart_change_url || '/cart/change';
  const CART_URL = routes.cart_url || '/cart';

  const strings = Object.assign(
    {
      playVideo: 'Play video',
      pauseVideo: 'Pause video',
      playBanner: 'Play scrolling banner',
      pauseBanner: 'Pause scrolling banner',
      cartUpdated: 'Bag updated',
      cartError: 'Sorry, we couldn’t update your bag. Please try again.',
    },
    ITK.strings || {}
  );

  const MOBILE_QUERY = '(max-width: 860px)'; // same breakpoint as Header.jsx and itk.css
  const MAX_QTY = 20; // the React site caps quantities at 20
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------ helpers */

  const $ = (selector, root) => (root || document).querySelector(selector);
  const $$ = (selector, root) => Array.prototype.slice.call((root || document).querySelectorAll(selector));

  const toInt = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
  };
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail: detail }));

  /** inert as an attribute, so CSS and older polyfills see it too. */
  function setInert(el, on) {
    if (!el) return;
    if (on) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }

  /** Parses an HTML string without running scripts or loading images. */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /** Same SVGs as snippets/icon.liquid (used when a toggle swaps its icon). */
  const ICON_PATHS = {
    play: '<path d="M8 5.5v13l11-6.5z"/>',
    pause: '<path d="M9 6v12M15 6v12"/>',
  };
  const icon = (name, size) =>
    '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    ICON_PATHS[name] + '</svg>';

  /** Play/pause label for a toggle: the button's data-label-play / data-label-pause, else the defaults. */
  const toggleLabel = (button, paused, playDefault, pauseDefault) =>
    paused
      ? button.getAttribute('data-label-play') || playDefault
      : button.getAttribute('data-label-pause') || pauseDefault;

  /**
   * Shows the play icon while paused and the pause icon while playing. Buttons that already hold
   * both icons (and let CSS pick one from .is-paused / .marquee--paused) are left alone.
   */
  function swapToggleIcon(button, paused, size) {
    if (button.querySelectorAll('svg').length > 1) return;
    button.innerHTML = icon(paused ? 'play' : 'pause', size);
  }

  /**
   * Scroll lock shared by the menu and the drawer: html.no-scroll stays on while either needs it.
   * (It sits on <html>, which owns the viewport scroll, exactly like useScrollLock in ui.jsx.)
   */
  const scrollLocks = new Set();
  function lockScroll(key, on) {
    if (on) scrollLocks.add(key);
    else scrollLocks.delete(key);
    document.documentElement.classList.toggle('no-scroll', scrollLocks.size > 0);
  }

  /**
   * Formats an amount in subunits (paise) with the shop's money format, like Liquid's `money`
   * filter. Round amounts drop ".00" when ITK.hideZeroDecimals is on (`money_without_trailing_zeros`).
   */
  function formatMoney(cents, format) {
    let fmt = format || ITK.moneyFormat || '{{amount}}';
    // the format may hold HTML (<span class=money>) or entities (&#8377;); reduce it to plain text
    if (/[<&]/.test(fmt)) fmt = parseHTML(fmt).body.textContent || fmt;

    const value = Number(cents) || 0;
    const hideZero = ITK.hideZeroDecimals !== false;
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';

    const build = (precision, thousands, decimal) => {
      const parts = (Math.abs(value) / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const fraction = parts[1] && !(hideZero && /^0+$/.test(parts[1])) ? decimal + parts[1] : '';
      return (value < 0 ? '-' : '') + whole + fraction;
    };

    let amount;
    switch (key) {
      case 'amount_no_decimals':
        amount = build(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = build(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = build(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = build(2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = build(0, ' ', '');
        break;
      case 'amount_with_space_separator':
        amount = build(2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        amount = build(2, ' ', '.');
        break;
      default:
        amount = build(2, ',', '.');
    }
    return match ? fmt.replace(match[0], amount) : amount;
  }

  /* ================================================================== 1. header */

  function updateHeaderSolid() {
    const header = $('.header');
    if (header) header.classList.toggle('header--solid', window.scrollY > 12);
  }

  let scrollQueued = false;
  window.addEventListener(
    'scroll',
    () => {
      if (scrollQueued) return;
      scrollQueued = true;
      window.requestAnimationFrame(() => {
        scrollQueued = false;
        updateHeaderSolid();
      });
    },
    { passive: true }
  );

  /* ================================================================== 2. mobile menu */

  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  let menuOpen = false;

  /** On phones the closed menu is out of the tab order and the accessibility tree. */
  function syncNavInert() {
    setInert(document.getElementById('site-nav'), mobileQuery.matches && !menuOpen);
  }

  function setMenu(open, options) {
    const opts = options || {};
    const header = $('.header');
    const nav = document.getElementById('site-nav');
    const button = $('.menu-btn');
    const scrim = $('.menu-scrim');
    const next = Boolean(open && header && nav);
    if (next === menuOpen) return;
    menuOpen = next;

    if (header) header.classList.toggle('header--menu', menuOpen);
    if (scrim) scrim.hidden = !menuOpen;
    if (button) {
      button.setAttribute('aria-expanded', String(menuOpen));
      $$('[data-itk-menu-icon]', button).forEach((el) => {
        el.hidden = (el.getAttribute('data-itk-menu-icon') === 'close') !== menuOpen;
      });
    }
    syncNavInert();
    lockScroll('menu', menuOpen);

    if (menuOpen) {
      // move focus into the opened menu
      const firstLink = nav.querySelector('a');
      if (firstLink) firstLink.focus();
    } else if (opts.returnFocus && button) {
      button.focus();
    }
  }

  function onMobileQueryChange() {
    if (!mobileQuery.matches && menuOpen) setMenu(false);
    syncNavInert();
  }
  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onMobileQueryChange);
  else if (mobileQuery.addListener) mobileQuery.addListener(onMobileQueryChange);

  /* ================================================================== 3. reveal on scroll */

  let revealObserver = null;

  function initReveal(root) {
    const items = $$('.reveal:not(.is-in)', root);
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        // a fixed-pixel margin works for elements taller than the viewport too
        { rootMargin: '0px 0px -60px 0px' }
      );
    }
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ================================================================== 4. bag drawer */

  let drawerOpen = false;
  let drawerReturnTo = null;

  const getDrawer = () => $('[data-itk-drawer]') || $('.drawer');
  const getPanel = () => {
    const drawer = getDrawer();
    return drawer ? drawer.querySelector('.drawer__panel') : null;
  };

  function openDrawer(trigger) {
    const drawer = getDrawer();
    if (!drawer) return false;
    if (drawerOpen) return true;
    drawerOpen = true;
    drawerReturnTo = trigger || document.activeElement;
    setInert(drawer, false);
    drawer.classList.add('drawer--open');
    lockScroll('drawer', true);
    const panel = getPanel();
    if (panel) panel.focus({ preventScroll: true });
    return true;
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer || !drawerOpen) return;
    drawerOpen = false;
    drawer.classList.remove('drawer--open');
    setInert(drawer, true);
    lockScroll('drawer', false);
    // hand focus back to whatever opened the drawer (usually the bag button)
    const back = drawerReturnTo && document.contains(drawerReturnTo) ? drawerReturnTo : $('[data-itk-open-cart]');
    drawerReturnTo = null;
    if (back && typeof back.focus === 'function') back.focus({ preventScroll: true });
  }

  /** Keeps keyboard focus inside the open drawer (same logic as CartDrawer.jsx). */
  function trapFocus(event) {
    const panel = getPanel();
    if (!panel) return;
    const els = $$(FOCUSABLE, panel).filter(
      (el) => el.offsetParent !== null && el.getAttribute('tabindex') !== '-1'
    );
    if (!els.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = els[0];
    const last = els[els.length - 1];
    const active = document.activeElement;
    if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** After the drawer contents are swapped, never leave focus stranded on <body>. */
  function keepFocusInDrawer() {
    const panel = getPanel();
    if (drawerOpen && panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
  }

  /* ================================================================== 5. AJAX cart */

  /** "shopify-section-sections--123__header" → "sections--123__header" */
  function sectionIdOf(el, fallback) {
    const wrapper = el && el.closest('.shopify-section');
    const prefix = 'shopify-section-';
    return wrapper && wrapper.id.indexOf(prefix) === 0 ? wrapper.id.slice(prefix.length) : fallback;
  }

  const drawerSectionId = () => sectionIdOf(getDrawer(), 'cart-drawer');
  // The header lives in a section group, so it has a dynamic id. Requesting that id (rather than
  // the "header" file name) renders it with the merchant's saved settings.
  const headerSectionId = () => sectionIdOf($('.header'), 'header');

  /** Sections re-rendered after every cart change (the API renders at most five per request). */
  function cartSectionIds() {
    const ids = [];
    if (getDrawer()) ids.push(drawerSectionId());
    if ($('.header')) ids.push(headerSectionId());
    $$('[data-itk-cart-section]').forEach((el) => {
      const id = el.getAttribute('data-itk-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.slice(0, 5);
  }

  /** Copies the bag count (and the bag button's label) from a freshly rendered header. */
  function updateHeaderCount(doc) {
    const fresh = doc.getElementById('itk-cart-count');
    const current = document.getElementById('itk-cart-count');
    if (!fresh || !current) return false;
    current.textContent = fresh.textContent;
    current.hidden = fresh.hidden;
    const freshButton = fresh.closest('[data-itk-open-cart]');
    const button = current.closest('[data-itk-open-cart]');
    if (freshButton && button && freshButton.hasAttribute('aria-label')) {
      button.setAttribute('aria-label', freshButton.getAttribute('aria-label'));
    }
    return true;
  }

  /** Fallback when the header HTML is not available: set the number from cart JSON. */
  function setCount(count) {
    const current = document.getElementById('itk-cart-count');
    if (!current || typeof count !== 'number') return;
    current.textContent = String(count);
    current.hidden = count === 0;
  }

  /** Lets the free-shipping bar slide from its old width to the new one after a re-render. */
  function animateShippingBar(panel, fromWidth) {
    const bar = panel.querySelector('.bar span');
    if (!bar || !fromWidth || bar.style.width === fromWidth) return;
    const to = bar.style.width;
    bar.style.transition = 'none';
    bar.style.width = fromWidth;
    void bar.offsetWidth; // flush the old width before animating
    bar.style.transition = '';
    bar.style.width = to;
  }

  /**
   * Applies a Section Rendering API payload ({ "<section id>": "<html>" }).
   * Only the drawer's panel contents and the bag count are swapped, so the open drawer, the
   * mobile menu state and any focus outside the drawer survive the update.
   * Returns { header: true } when the header count was updated.
   */
  function renderSections(sections) {
    const result = { header: false };
    if (!sections) return result;

    const drawerHtml = sections[drawerSectionId()];
    const panel = getPanel();
    if (drawerHtml && panel) {
      const fresh = parseHTML(drawerHtml).querySelector('.drawer__panel');
      if (fresh) {
        const oldBar = panel.querySelector('.bar span');
        const oldWidth = oldBar ? oldBar.style.width : '';
        panel.innerHTML = fresh.innerHTML;
        animateShippingBar(panel, oldWidth);
      }
    }

    const headerHtml = sections[headerSectionId()];
    if (headerHtml) result.header = updateHeaderCount(parseHTML(headerHtml));

    // optional extra regions (e.g. the /cart page) that opt in with data-itk-cart-section="<section id>"
    $$('[data-itk-cart-section]').forEach((el) => {
      const id = el.getAttribute('data-itk-cart-section');
      if (!sections[id]) return;
      const selector = '[data-itk-cart-section="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]';
      const fresh = parseHTML(sections[id]).querySelector(selector);
      if (fresh) {
        el.innerHTML = fresh.innerHTML;
        initSteppers(el);
        initReveal(el);
      }
    });

    return result;
  }

  /** Announces cart updates to screen readers via the drawer's status region. */
  function announce(message) {
    const status = $('[data-itk-cart-status]');
    if (!status || !message) return;
    status.textContent = '';
    // a short delay so the (possibly just inserted) live region is noticed
    window.setTimeout(() => {
      status.textContent = message;
    }, 120);
  }

  /** POSTs to a cart endpoint; throws an Error with .status/.data on failure. */
  async function cartRequest(url, body) {
    const isForm = body instanceof FormData;
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: isForm
        ? { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
        : { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: isForm ? body : JSON.stringify(body),
    });
    let data = null;
    try {
      data = await response.json();
    } catch (err) {
      data = null; // not JSON (e.g. an HTML error page)
    }
    if (!response.ok || !data) {
      const error = new Error((data && (data.description || data.message)) || 'Cart request failed (' + response.status + ')');
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  }

  /** Message for a cart error the shopper can act on (e.g. "only 3 left"), or null. */
  function userFacingError(error) {
    if (!error || !error.data || (error.status !== 422 && error.status !== 400)) return null;
    const description = error.data.description;
    if (typeof description === 'string' && description) return description;
    return typeof error.data.message === 'string' && error.data.message ? error.data.message : null;
  }

  /* ---------- add to bag: form[data-itk-add] */

  function setFormBusy(form, busy) {
    form.setAttribute('aria-busy', String(busy));
    Array.prototype.forEach.call(form.elements, (el) => {
      if (el.type !== 'submit') return;
      if (busy) {
        if (el.disabled) return;
        el.disabled = true;
        el.setAttribute('data-itk-busy', '');
      } else if (el.hasAttribute('data-itk-busy')) {
        el.disabled = false;
        el.removeAttribute('data-itk-busy');
      }
    });
  }

  function clearFormError(form) {
    const el = form.querySelector('[data-itk-error]') || (form.nextElementSibling && form.nextElementSibling.matches('.itk-form-error') ? form.nextElementSibling : null);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function showFormError(form, message, scrollIntoView) {
    let el = form.querySelector('[data-itk-error]');
    if (!el && form.nextElementSibling && form.nextElementSibling.matches('.itk-form-error')) el = form.nextElementSibling;
    if (!el) {
      el = document.createElement('p');
      el.className = 'itk-form-error';
      el.setAttribute('role', 'alert');
      form.insertAdjacentElement('afterend', el);
    }
    el.hidden = false;
    el.textContent = message;
    if (scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  async function addToCart(form, submitter) {
    if (form.getAttribute('aria-busy') === 'true') return;
    const trigger = form.itkTrigger || submitter || document.activeElement;
    const fromBuyBar = Boolean(form.itkTrigger);
    form.itkTrigger = null;

    const body = new FormData(form);
    body.append('sections', cartSectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    setFormBusy(form, true);
    clearFormError(form);
    try {
      const item = await cartRequest(CART_ADD_URL + '.js', body);
      const rendered = renderSections(item.sections);
      if (!rendered.header) refreshCount();
      setMenu(false);
      const wasOpen = drawerOpen;
      openDrawer(trigger);
      if (wasOpen) keepFocusInDrawer(); // e.g. the upsell "Add" button was replaced by the re-render
      announce(strings.cartUpdated);
      emit('itk:cart:add', { item: item, form: form });
      emit('itk:cart-updated', { item: item, source: 'drawer' }); // listened to by the /cart page section
    } catch (error) {
      const message = userFacingError(error);
      if (message) {
        showFormError(form, message, fromBuyBar);
      } else {
        // network failure, missing endpoint, unexpected response: let the browser post the form
        console.warn('[itk] add to bag failed, submitting the form normally:', error);
        setFormBusy(form, false);
        form.submit(); // HTMLFormElement.submit() skips the submit event, so no loop
        return;
      }
    } finally {
      if (document.contains(form)) setFormBusy(form, false);
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-itk-add')) return;
    // no drawer on this page (or a very old browser): keep the normal form post to /cart/add
    if (!getDrawer() || !window.fetch || !window.FormData || !window.DOMParser) return;
    event.preventDefault();
    addToCart(form, event.submitter);
  });

  /* ---------- change a line: [data-itk-line][data-itk-qty] */

  let changeInFlight = false;

  /** After a re-render, focus the equivalent control on the same line (or the panel). */
  function focusAfterChange(line, action, quantity) {
    const panel = getPanel();
    if (!panel) return;
    let target = null;
    if (quantity > 0 && action) {
      target = panel.querySelector('[data-itk-line="' + line + '"][data-itk-action="' + action + '"]');
      if (target && target.disabled) target = panel.querySelector('[data-itk-line="' + line + '"]:not([disabled])');
    }
    (target || panel).focus({ preventScroll: true });
  }

  async function changeLine(button) {
    if (changeInFlight) return;
    const line = toInt(button.getAttribute('data-itk-line'), 0);
    const quantity = toInt(button.getAttribute('data-itk-qty'), -1);
    if (line < 1 || quantity < 0) return;

    const panel = getPanel();
    const inDrawer = Boolean(panel && panel.contains(button));
    const inSection = Boolean(button.closest('[data-itk-cart-section]'));
    const focusWasInDrawer = Boolean(panel && panel.contains(document.activeElement));
    const action = button.getAttribute('data-itk-action') || '';
    const busyEl = inDrawer ? panel : button.closest('[data-itk-cart-section]') || button;

    changeInFlight = true;
    busyEl.setAttribute('aria-busy', 'true');
    try {
      const cart = await cartRequest(CART_CHANGE_URL + '.js', {
        line: line,
        quantity: quantity,
        sections: cartSectionIds(),
        sections_url: window.location.pathname,
      });
      const rendered = renderSections(cart.sections);
      if (!rendered.header) setCount(cart.item_count);
      if (inDrawer && focusWasInDrawer) focusAfterChange(line, action, quantity);
      else keepFocusInDrawer();
      announce(strings.cartUpdated);
      emit('itk:cart:change', { cart: cart, line: line, quantity: quantity });
      emit('itk:cart-updated', { cart: cart, source: 'drawer' });
      // a control outside the drawer that no section re-render covers (e.g. on the /cart page):
      // reload so the page shows the new cart
      if (!inDrawer && !inSection) window.location.reload();
    } catch (error) {
      const message = userFacingError(error);
      if (message && document.contains(button)) {
        const host = button.closest('.drawer__info') || button.closest('li') || button.parentElement;
        let el = host.querySelector('.itk-form-error');
        if (!el) {
          el = document.createElement('p');
          el.className = 'itk-form-error';
          el.setAttribute('role', 'alert');
          host.appendChild(el);
        }
        el.textContent = message;
      } else {
        console.warn('[itk] bag update failed, falling back to a page load:', error);
        window.location.href = CART_CHANGE_URL + '?line=' + line + '&quantity=' + quantity;
      }
    } finally {
      changeInFlight = false;
      busyEl.removeAttribute('aria-busy');
    }
  }

  /* ---------- refresh (bfcache restore, or on demand) */

  async function refreshCount() {
    try {
      const response = await fetch(CART_URL + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.ok) setCount((await response.json()).item_count);
    } catch (error) {
      /* the count simply stays as it was */
    }
  }

  async function refreshCart() {
    const ids = cartSectionIds();
    if (!ids.length || !window.fetch) return;
    try {
      const response = await fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), {
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      const sections = await response.json();
      const rendered = renderSections(sections);
      if (!rendered.header) refreshCount();
      keepFocusInDrawer();
      emit('itk:cart:refresh', { sections: sections });
    } catch (error) {
      /* keep what is on screen */
    }
  }

  // Back/forward cache: the bag may have changed on another page since this one was frozen.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    setMenu(false);
    refreshCart();
  });

  /* ================================================================== 6. quantity steppers */

  /** The parts of a .qty stepper: a number input or an <output>, plus the quantity field to keep in sync. */
  function stepperParts(box) {
    const input = box.querySelector('input[type="number"]');
    const output = box.querySelector('output');
    let field = input || box.querySelector('input[name="quantity"]');
    let form = (input && input.form) || (output && output.form) || box.closest('form');
    if (!form && box.getAttribute('data-form')) form = document.getElementById(box.getAttribute('data-form'));
    if (!field && form) field = form.querySelector('input[name="quantity"]');
    const min = toInt(input && input.min, toInt(box.getAttribute('data-min'), 1));
    const max = toInt(input && input.max, toInt(box.getAttribute('data-max'), MAX_QTY));
    return { input: input, output: output, field: field, form: form, min: min, max: Math.max(min, max) };
  }

  function readStepper(parts) {
    if (parts.input) return toInt(parts.input.value, parts.min);
    if (parts.output) return toInt(parts.output.value || parts.output.textContent, parts.min);
    if (parts.field) return toInt(parts.field.value, parts.min);
    return parts.min;
  }

  function syncStepperButtons(box, parts, value) {
    $$('[data-qty-step]', box).forEach((button) => {
      const step = toInt(button.getAttribute('data-qty-step'), 0);
      button.disabled = step < 0 ? value <= parts.min : value >= parts.max;
    });
  }

  /**
   * Prices that follow the quantity: any element with data-itk-unit-price="<subunits>" inside
   * the stepper's form, inside the nearest [data-itk-qty-scope], or anywhere with
   * data-itk-form="<form id>" (e.g. the sticky buy bar), shows unit price × quantity.
   */
  function updateQtyPrices(box, parts, value) {
    const targets = new Set();
    if (parts.form) {
      $$('[data-itk-unit-price]', parts.form).forEach((el) => targets.add(el));
      if (parts.form.id) {
        $$('[data-itk-unit-price][data-itk-form="' + parts.form.id + '"]').forEach((el) => targets.add(el));
      }
    }
    const scope = box.closest('[data-itk-qty-scope]');
    if (scope) $$('[data-itk-unit-price]', scope).forEach((el) => targets.add(el));
    targets.forEach((el) => {
      const unit = toInt(el.getAttribute('data-itk-unit-price'), NaN);
      if (!Number.isNaN(unit)) el.textContent = formatMoney(unit * value);
    });
  }

  function setStepper(box, value) {
    const parts = stepperParts(box);
    const next = clamp(value, parts.min, parts.max);
    if (parts.input) parts.input.value = String(next);
    if (parts.output) parts.output.value = String(next);
    if (parts.field && parts.field !== parts.input) parts.field.value = String(next);
    syncStepperButtons(box, parts, next);
    updateQtyPrices(box, parts, next);
    if (parts.input) parts.input.dispatchEvent(new Event('input', { bubbles: true }));
    box.dispatchEvent(new CustomEvent('itk:qty-change', { bubbles: true, detail: { quantity: next, form: parts.form } }));
    return next;
  }

  function initSteppers(root) {
    $$('.qty', root).forEach((box) => {
      if (!box.querySelector('[data-qty-step]')) return;
      const parts = stepperParts(box);
      syncStepperButtons(box, parts, clamp(readStepper(parts), parts.min, parts.max));
    });
  }

  // typing straight into a number input: clamp it when the value is committed
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;
    const box = input.closest('.qty');
    if (box && box.querySelector('[data-qty-step]')) {
      const parts = stepperParts(box);
      setStepper(box, readStepper(parts));
    }
  });

  /* ================================================================== 7. sticky buy bar (product page) */

  let buyBarObserver = null;
  let buyBarWatcher = null;

  /**
   * What the buy bar must never cover: the cross-sell card, the FAQ and reviews sections, the
   * write-a-review panel and the footer. Other sections can opt in with [data-buybar-block].
   */
  const BUYBAR_BLOCKERS =
    '.footer, .pdp__other, #reviews, #write-review, .itk-faq, [data-buybar-block], [data-itk-buybar-blocker]';

  /**
   * The write-a-review panel (#write-review in sections/itk-product-reviews) is open: shown (no `hidden`,
   * laid out), or a <details>/<dialog> that is open, or a toggle for it that says expanded.
   */
  function writeReviewOpen() {
    const panel = document.getElementById('write-review');
    if (!panel) return false;
    if (panel.tagName === 'DETAILS' || panel.tagName === 'DIALOG') return panel.open;
    if (document.querySelector('[aria-controls="write-review"][aria-expanded="true"]')) return true;
    return !panel.hidden && panel.getClientRects().length > 0;
  }

  /**
   * Shows .buybar only once both the price row (.pdp__price) and the buy row (.pdp__buy) have
   * scrolled up past the top of the screen, and never while a blocker is in view or the
   * write-a-review panel is open. inert and aria-hidden follow the visibility.
   */
  function initBuyBar() {
    if (buyBarObserver) {
      buyBarObserver.disconnect();
      buyBarObserver = null;
    }
    if (buyBarWatcher) {
      buyBarWatcher.disconnect();
      buyBarWatcher = null;
    }
    // body.itk-buybar-on lifts Shopify's hCaptcha badge above the bar (assets/itk.css)
    document.body.classList.remove('itk-buybar-on');
    const bar = $('.buybar');
    if (!bar) return;
    const setShown = (show) => {
      bar.classList.toggle('buybar--show', show);
      setInert(bar, !show);
      bar.setAttribute('aria-hidden', String(!show));
      // the bar's height (safe area included), so the badge clears even a taller bar (large text)
      if (show && bar.offsetHeight) document.body.style.setProperty('--itk-buybar-h', bar.offsetHeight + 'px');
      document.body.classList.toggle('itk-buybar-on', show);
    };
    setShown(false);
    const buy = $('.pdp__buy');
    if (!buy || !('IntersectionObserver' in window)) return;

    const anchors = [$('.pdp__price'), buy].filter(Boolean);
    const above = new Map(anchors.map((el) => [el, false]));
    // a blocker that wraps the buy row (or the bar) would hide the bar for good: skip those
    const blockers = $$(BUYBAR_BLOCKERS).filter((el) => !el.contains(buy) && !el.contains(bar) && !bar.contains(el));
    const blocked = new Set();
    const update = () => {
      let allAbove = true;
      above.forEach((isAbove) => {
        if (!isAbove) allAbove = false;
      });
      setShown(allAbove && blocked.size === 0 && !writeReviewOpen());
    };

    buyBarObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (above.has(entry.target)) {
          // rootBounds is null inside a cross-origin frame (the theme editor): the viewport top is 0 there
          const top = entry.rootBounds ? entry.rootBounds.top : 0;
          above.set(entry.target, !entry.isIntersecting && entry.boundingClientRect.bottom <= top);
        } else if (entry.isIntersecting) {
          blocked.add(entry.target);
        } else {
          blocked.delete(entry.target);
        }
      });
      update();
    });
    anchors.forEach((el) => buyBarObserver.observe(el));
    blockers.forEach((el) => buyBarObserver.observe(el));

    // the write-a-review panel can open without any scrolling: re-check when it or its toggle changes
    const panel = document.getElementById('write-review');
    if (panel && 'MutationObserver' in window) {
      buyBarWatcher = new MutationObserver(update);
      buyBarWatcher.observe(panel, { attributes: true, attributeFilter: ['open', 'hidden', 'class', 'aria-hidden'] });
      $$('[aria-controls="write-review"]').forEach((toggle) =>
        buyBarWatcher.observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] })
      );
    }
  }

  /**
   * Links marked data-itk-focus (the buy bar's "Notify me" on a coming-soon product) scroll to the
   * field their #hash names and focus it. Returns true when handled here.
   */
  function focusFromLink(link) {
    let id = (link.hash || '').slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (error) {
      /* keep the raw id */
    }
    const field = id ? document.getElementById(id) : null;
    if (!field || field.closest('[inert]')) return false;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    field.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    field.focus({ preventScroll: true });
    return true;
  }

  /** The main product form: the one that is (or holds, or wraps) .pdp__buy. */
  function mainProductForm() {
    const buy = $('.pdp__buy');
    if (buy) {
      if (buy instanceof HTMLFormElement) return buy;
      const form = buy.closest('form') || buy.querySelector('form');
      if (form) return form;
    }
    return $('.pdp form[data-itk-add]') || $('form[data-itk-add][data-itk-main]');
  }

  /** Clicking the buy bar's button submits the main product form. Returns true when handled here. */
  function submitFromBuyBar(button) {
    // a submit button that already belongs to a form (inside it, or via form="…") submits natively
    if (button.form && button.type === 'submit') return false;
    const form = mainProductForm();
    if (!form) return false;
    form.itkTrigger = button;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else if (form.hasAttribute('data-itk-add') && getDrawer() && window.fetch) {
      addToCart(form, button);
    } else {
      form.itkTrigger = null;
      form.submit();
    }
    return true;
  }

  /* ================================================================== 8. unboxing video */

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function playVideo(video) {
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  }

  function loadVideo(video) {
    if (!video.getAttribute('src') && video.getAttribute('data-src')) video.src = video.getAttribute('data-src');
  }

  function syncVideoToggle(button, paused) {
    if (!button) return;
    button.classList.toggle('is-paused', paused);
    button.setAttribute('aria-label', toggleLabel(button, paused, strings.playVideo, strings.pauseVideo));
    swapToggleIcon(button, paused, 18);
  }

  /** Loads the video only when it comes near the viewport; plays only while visible. */
  function initVideos(root) {
    $$('.unbox__phone video[data-src]', root).forEach((video) => {
      if (video.itkState) return;
      const state = (video.itkState = { paused: reducedMotion.matches });
      const host = video.closest('.unbox__phone') || video.parentElement;
      syncVideoToggle(host.querySelector('.vid-toggle'), state.paused);

      if (!('IntersectionObserver' in window)) {
        loadVideo(video);
        if (!state.paused) playVideo(video);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (entry.isIntersecting) {
            loadVideo(video);
            if (!state.paused) playVideo(video);
          } else {
            video.pause();
          }
        },
        { rootMargin: '200px 0px' }
      );
      observer.observe(video);
    });
  }

  function toggleVideo(button) {
    const host = button.closest('.unbox__phone') || button.parentElement;
    const video = host && host.querySelector('video');
    if (!video) return;
    const state = video.itkState || (video.itkState = { paused: false });
    if (video.paused) {
      loadVideo(video);
      playVideo(video);
      state.paused = false;
    } else {
      video.pause();
      state.paused = true;
    }
    syncVideoToggle(button, state.paused);
  }

  /* ================================================================== 9. marquee */

  function toggleMarquee(button) {
    const marquee = button.closest('.marquee');
    if (!marquee) return;
    const paused = !marquee.classList.contains('marquee--paused');
    marquee.classList.toggle('marquee--paused', paused);
    button.setAttribute('aria-pressed', String(paused));
    button.setAttribute('aria-label', toggleLabel(button, paused, strings.playBanner, strings.pauseBanner));
    swapToggleIcon(button, paused, 16);
  }

  /** .marquee--off (itk.css) pauses the banner while it is out of view, so it does no work under the 3D scenes. */
  let marqueeObserver = null;

  function initMarquees(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!marqueeObserver) {
      marqueeObserver = new IntersectionObserver(
        // one batch can hold several changes for the same banner; the last one wins
        (entries) => entries.forEach((entry) => entry.target.classList.toggle('marquee--off', !entry.isIntersecting)),
        { rootMargin: '100px 0px' }
      );
    }
    $$('.marquee', root).forEach((el) => marqueeObserver.observe(el));
  }

  /* ================================================================== 10. footer newsletter notice */

  /**
   * Liquid reports a customer-form post (?customer_posted=true) to every customer form on the page, so
   * a "Notify me" sign-up on a product page would also show the footer's thank-you line. Shopify's
   * redirect keeps the posted form's #id, so the footer line ([data-itk-newsletter-done], rendered by
   * sections/footer.liquid) stays only when the hash names the footer form (#itk-newsletter); the same
   * goes for the footer's error line. With no hash at all (it can get lost, e.g. after a captcha step)
   * nothing is removed, so a real footer sign-up never loses its confirmation.
   */
  function newsletterHash(form) {
    if (!form) return '#itk-newsletter';
    const actionHash = (form.getAttribute('action') || '').split('#')[1];
    return '#' + (actionHash || form.id || 'itk-newsletter');
  }

  function initNewsletterNotice() {
    const hash = window.location.hash;
    if (!hash) return;
    $$('[data-itk-newsletter-done]').forEach((notice) => {
      const form = notice.closest('form') || document.getElementById('itk-newsletter');
      if (hash !== newsletterHash(form)) notice.remove();
    });
    // a failed post of another customer form would flag the footer field as well
    const form = document.getElementById('itk-newsletter');
    if (!form || hash === newsletterHash(form)) return;
    $$('.footer__error, [data-itk-newsletter-error]', form).forEach((el) => el.remove());
    $$('input[aria-invalid]', form).forEach((input) => {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    });
  }

  /* ================================================================== 11. 3D poster spinners */

  /**
   * Every 3D island ([data-itk-island]) shows a poster with a spinner until its scene is ready, but a scene is
   * only created once it is within 300px of the viewport (src/three/Scenes.jsx), so islands further down would
   * spin from page load on. .itk-island--far (itk.css) holds their spinner still until they come that near.
   */
  let islandObserver = null;

  function initIslandSpinners(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!islandObserver) {
      islandObserver = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.target.classList.toggle('itk-island--far', !entry.isIntersecting)),
        { rootMargin: '300px 0px' }
      );
    }
    $$('[data-itk-island]', root).forEach((el) => islandObserver.observe(el));
  }

  /* ================================================================== event wiring */

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // bag line quantity / remove buttons
    const lineButton = target.closest('[data-itk-line][data-itk-qty]');
    if (lineButton) {
      event.preventDefault();
      if (!lineButton.disabled && window.fetch) changeLine(lineButton);
      else if (!lineButton.disabled) {
        window.location.href =
          CART_CHANGE_URL + '?line=' + lineButton.getAttribute('data-itk-line') + '&quantity=' + lineButton.getAttribute('data-itk-qty');
      }
      return;
    }

    // quantity steppers
    const stepButton = target.closest('[data-qty-step]');
    if (stepButton) {
      event.preventDefault();
      const box = stepButton.closest('.qty');
      if (box && !stepButton.disabled) {
        const parts = stepperParts(box);
        setStepper(box, readStepper(parts) + toInt(stepButton.getAttribute('data-qty-step'), 0));
      }
      return;
    }

    // open the bag (modifier-clicks on the bag link still open /cart in a new tab)
    const opener = target.closest('[data-itk-open-cart]');
    if (opener) {
      if (getDrawer() && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        setMenu(false);
        openDrawer(opener);
      }
      return;
    }

    if (target.closest('[data-itk-close-cart]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    // mobile menu
    if (target.closest('.menu-btn')) {
      setMenu(!menuOpen);
      return;
    }
    if (target.closest('.menu-scrim')) {
      setMenu(false);
      return;
    }
    if (menuOpen && target.closest('#site-nav a')) {
      setMenu(false); // the link still navigates (or scrolls to its #hash)
      return;
    }

    const marqueeButton = target.closest('.marquee__toggle');
    if (marqueeButton) {
      toggleMarquee(marqueeButton);
      return;
    }

    const videoButton = target.closest('.vid-toggle');
    if (videoButton) {
      toggleVideo(videoButton);
      return;
    }

    const focusLink = target.closest('a[data-itk-focus]');
    if (focusLink) {
      if (focusFromLink(focusLink)) event.preventDefault();
      return;
    }

    const buyBarButton = target.closest('.buybar button:not([data-qty-step])');
    if (buyBarButton && submitFromBuyBar(buyBarButton)) event.preventDefault();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      if (drawerOpen) {
        closeDrawer();
      } else if (menuOpen) {
        // Escape closes the menu and returns focus to the menu button
        setMenu(false, { returnFocus: true });
      }
      return;
    }
    if (event.key === 'Tab' && drawerOpen) trapFocus(event);
    else if (event.key === 'Tab' && menuOpen) trapMenuFocus(event);
  });

  /** While the phone menu is open, Tab cycles through the header (links, bag, close) only. */
  function trapMenuFocus(event) {
    const header = document.querySelector('.header');
    if (!header) return;
    const items = Array.from(header.querySelectorAll('a[href], button:not([disabled])')).filter(
      (el) => el.offsetParent !== null && !el.closest('[inert]')
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!header.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- theme editor */

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (root.querySelector('.header')) {
      menuOpen = false;
      lockScroll('menu', false);
      syncNavInert();
      updateHeaderSolid();
    }
    if (root.querySelector('[data-itk-drawer]')) {
      drawerOpen = false; // the re-rendered drawer starts closed
      lockScroll('drawer', false);
    }
    initReveal(root);
    initSteppers(root);
    initVideos(root);
    initMarquees(root);
    initIslandSpinners(root);
    initBuyBar();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-itk-drawer]')) {
      drawerOpen = false;
      lockScroll('drawer', false);
    }
    if (event.target.querySelector('.header')) {
      menuOpen = false;
      lockScroll('menu', false);
    }
    if (marqueeObserver) $$('.marquee', event.target).forEach((el) => marqueeObserver.unobserve(el));
    if (islandObserver) $$('[data-itk-island]', event.target).forEach((el) => islandObserver.unobserve(el));
  });

  // selecting the cart drawer in the editor opens it so it can be edited
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('[data-itk-drawer]')) openDrawer();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.querySelector('[data-itk-drawer]')) closeDrawer();
  });

  /* ---------- start */

  function init() {
    updateHeaderSolid();
    syncNavInert();
    initReveal(document);
    initSteppers(document);
    initBuyBar();
    initVideos(document);
    initMarquees(document);
    initIslandSpinners(document);
    initNewsletterNotice();
  }

  // the script is loaded with `defer`, so the document is parsed already; guard for other loaders
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  ITK.cart = { open: openDrawer, close: closeDrawer, refresh: refreshCart, sectionIds: cartSectionIds };
  ITK.formatMoney = formatMoney;
  ITK.lockScroll = lockScroll;
})();
